import type { FileMonitoringState } from '../file-integrity-monitoring/useFileMonitoringData';
import { normalizeFimKey } from '../file-integrity-monitoring/fileMonitoringDetailModel';
import type {
  WazuhFimAgentSummaryData,
  WazuhFimEventItem,
} from 'src/types/mindtime/wazuhFileMonitoring';
import { previewPaged } from './previewUtils';

export const PREVIEW_FIM_IDS = ['preview-fim-1', 'preview-fim-2'] as const;

const PREVIEW_EVENTS: WazuhFimEventItem[] = [
  {
    id: 'preview-fim-1',
    timestamp: '2026-08-28T11:02:00.000Z',
    changeType: 'modified',
    filePath: 'C:\\Windows\\System32\\drivers\\etc\\hosts',
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    severity: 'high',
    user: 'SYSTEM',
    systemWazuhAgentId: 'preview-endpoint-1',
    hostName: 'fin-srv-02.acme.local',
  },
  {
    id: 'preview-fim-2',
    timestamp: '2026-08-28T09:18:00.000Z',
    changeType: 'added',
    filePath: '/etc/cron.d/suspicious-job',
    endpoint: 'APP-SERVER-01',
    organization: 'Nexus Dynamics',
    severity: 'critical',
    user: 'root',
    systemWazuhAgentId: 'preview-endpoint-3',
    hostName: 'app-server-01.nexus.local',
  },
  {
    id: 'preview-fim-3',
    timestamp: '2026-08-27T20:45:00.000Z',
    changeType: 'deleted',
    filePath: 'C:\\Program Files\\App\\config.ini',
    endpoint: 'HR-PC-17',
    organization: 'Vertex Solutions',
    severity: 'medium',
    user: 'j.smith',
    systemWazuhAgentId: 'preview-endpoint-2',
    hostName: 'hr-pc-17.vertex.local',
  },
];

const buildAgentPreviewSummary = (
  agentId: string,
  items: WazuhFimEventItem[],
): WazuhFimAgentSummaryData => {
  const breakdown = { added: 0, modified: 0, deleted: 0 };
  const severityByRules = { total: 0, critical: 0, high: 0, medium: 0 };
  let criticalFileAlerts = 0;
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  let fileChangesToday = 0;

  items.forEach((item) => {
    const changeKey = normalizeFimKey(item.changeType);
    if (changeKey === 'added') breakdown.added += 1;
    else if (changeKey === 'modified') breakdown.modified += 1;
    else if (changeKey === 'deleted') breakdown.deleted += 1;

    const severityKey = normalizeFimKey(item.severity);
    severityByRules.total += 1;
    if (severityKey === 'critical') {
      severityByRules.critical += 1;
      criticalFileAlerts += 1;
    } else if (severityKey === 'high') {
      severityByRules.high += 1;
    } else if (severityKey === 'medium') {
      severityByRules.medium += 1;
    }

    const timestamp = new Date(item.timestamp).getTime();
    if (!Number.isNaN(timestamp) && now - timestamp <= dayMs) {
      fileChangesToday += 1;
    }
  });

  return {
    systemWazuhAgentId: agentId,
    changeTypeBreakdown: breakdown,
    fileChangesToday: { total: fileChangesToday },
    criticalFileAlerts: { total: criticalFileAlerts },
    severityByRules,
  };
};

export const getFileIntegrityPreviewState = (): FileMonitoringState => ({
  loading: false,
  summary: {
    monitoredFiles: { total: 12450, endpointCount: 24 },
    fileChangesToday: { total: 38, endpointCount: 12 },
    changeTypeBreakdown: { modified: 18, added: 12, deleted: 8 },
    criticalFileAlerts: { total: 4, endpointCount: 3 },
    severityByRules: { total: 38, critical: 4, high: 10, medium: 14 },
  },
  items: previewPaged(PREVIEW_EVENTS, 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-1',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
        organization: 'Acme Financial',
        monitoredFiles: { total: 4200 },
        fileChangesToday: { total: 12 },
        changeTypeBreakdown: { added: 3, modified: 7, deleted: 2 },
        criticalFileAlerts: { total: 1 },
        severityByRules: { total: 12, critical: 1, high: 4, medium: 5 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organization: 'Nexus Dynamics',
        monitoredFiles: { total: 3100 },
        fileChangesToday: { total: 9 },
        changeTypeBreakdown: { added: 4, modified: 4, deleted: 1 },
        criticalFileAlerts: { total: 2 },
        severityByRules: { total: 9, critical: 2, high: 3, medium: 3 },
      },
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        monitoredFiles: { total: 1800 },
        fileChangesToday: { total: 5 },
        changeTypeBreakdown: { added: 1, modified: 2, deleted: 2 },
        criticalFileAlerts: { total: 0 },
        severityByRules: { total: 5, critical: 0, high: 1, medium: 3 },
      },
    ],
    0,
    10,
  ),
});

export const getFileIntegrityDetailPreview = (id: string): WazuhFimEventItem | null =>
  PREVIEW_EVENTS.find((row) => row.id === id) ?? PREVIEW_EVENTS[0] ?? null;

export const getAgentFileMonitoringPreview = (
  systemWazuhAgentId: string,
): { summary: WazuhFimAgentSummaryData; items: WazuhFimEventItem[] } => {
  const agentId = systemWazuhAgentId.trim();
  const items = PREVIEW_EVENTS.filter((row) => row.systemWazuhAgentId === agentId);
  const list = items.length ? items : PREVIEW_EVENTS.slice(0, 1);

  return {
    summary: buildAgentPreviewSummary(agentId, list),
    items: list,
  };
};
